import { useState, useEffect, useMemo } from "react";
import { contentfulClient } from "../services/api.ts";
import { GetNavigation } from "../queries/GetNavigation.ts";
import { GetFooter } from "../queries/GetFooter.ts";
import { navigationTransformer } from "../components/cms-components/C100Navigation/C100Navigation.transformer.ts";
import { footerTransformer } from "../components/cms-components/C102Footer/C102Footer.transformer.ts";
import {
  FooterContentFragment,
  NavigationContentFragment,
} from "../graphql/graphql.ts";
import { C100NavigationProps } from "../components/cms-components/C100Navigation/C100Navigation.tsx";
import { C102FooterProps } from "../components/cms-components/C102Footer/C102Footer.tsx";
import { useGlobalContext } from "../utils/GlobalContext.ts";
import validateAndFormatLocale from "../utils/validateAndFormatLocale.ts";

interface UseGlobalLayoutReturn {
  navigationData: C100NavigationProps | null;
  footerData: C102FooterProps | null;
  loading: boolean;
  error: boolean;
  currentLocale: string | null;
}

export function useGlobalLayout(): UseGlobalLayoutReturn {
  const { localeConfig } = useGlobalContext();
  const [navigationData, setNavigationData] =
    useState<C100NavigationProps | null>(null);
  const [footerData, setFooterData] = useState<C102FooterProps | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Get current locale from URL path
  const currentLocale = useMemo(
    () =>
      validateAndFormatLocale(
        window.location.pathname.split("/")[1] ?? localeConfig.defaultLocale,
        localeConfig.locales,
      ),
    [localeConfig.defaultLocale, localeConfig.locales],
  );

  useEffect(() => {
    async function fetchGlobalLayoutData() {
      try {
        setLoading(true);
        setError(false);

        const [navigationResponse, footerResponse] = await Promise.all([
          contentfulClient.query(GetNavigation, {
            locale: currentLocale ?? localeConfig.defaultLocale,
          }),
          contentfulClient.query(GetFooter, {
            locale: currentLocale ?? localeConfig.defaultLocale,
          }),
        ]);

        setNavigationData(
          navigationTransformer(
            navigationResponse.navigationCollection
              ?.items[0] as NavigationContentFragment,
            localeConfig,
          ),
        );

        setFooterData(
          footerTransformer(
            footerResponse.footerCollection?.items[0] as FooterContentFragment,
            localeConfig,
          ),
        );
      } catch (err) {
        console.error("Error fetching global layout data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    fetchGlobalLayoutData();
  }, [currentLocale, localeConfig, localeConfig.defaultLocale]);

  return {
    navigationData,
    footerData,
    loading,
    error,
    currentLocale,
  };
}
